import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LESSONS } from '@/seed/lessons';
import { findModule } from '@/seed/modules';
import { AVATAR_IDS } from '@/constants/avatars';
import { COACH } from '@/constants/coach';
import AvatarMount, { type AvatarHandle } from '@/components/avatar/AvatarMount';
import AskCoachInput from '@/components/avatar/AskCoachInput';
import LiveCaption from '@/components/avatar/LiveCaption';
import MetaCueChip from '@/components/avatar/MetaCueChip';
import ChatTranscript, { type ChatTurn } from '@/components/avatar/ChatTranscript';
import StartSessionModal from '@/components/StartSessionModal';
import { useAvatarMic } from '@/lib/mic/useAvatarMic';
import { buildLessonStartData } from '@/lib/codebaby/startData';
import type { CoachMeta } from '@/lib/codebaby';
import { evaluateCheckIn, type CheckInVerdict } from '@/lib/lesson/checkin';
import { useLearner } from '@/state/learnerStore';
import type {
  CheckInBeat,
  CoachingMomentBeat,
  Lesson,
  LessonBeat,
  TeachingBeat,
  VisualAnchor,
} from '@/types/lesson';
import './LessonScreen.css';

export default function LessonScreen() {
  const { lessonId } = useParams();
  const lesson = lessonId ? LESSONS[lessonId] : undefined;
  if (!lesson) return <LessonMissing lessonId={lessonId} />;
  return <LessonInterior lesson={lesson} />;
}

function LessonMissing({ lessonId }: { lessonId: string | undefined }) {
  return (
    <div className="lesson-missing">
      <p className="eyebrow">404</p>
      <h1>No lesson with id {lessonId ?? '—'}.</h1>
      <p>
        <Link to="/">Back to dashboard</Link>
      </p>
    </div>
  );
}

/* ------------------------------ Interior ------------------------------ */

function LessonInterior({ lesson }: { lesson: Lesson }) {
  const learner = useLearner();
  const coachRef = useRef<AvatarHandle>(null);

  const [beatIndex, setBeatIndex] = useState(0);
  const [speed, setSpeed] = useState<'1x' | '1.25x'>('1x');
  const [askInput, setAskInput] = useState('');

  // Avatar lifecycle state
  const [coachReady, setCoachReady] = useState(false);
  const [coachSpeaking, setCoachSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);

  const mic = useAvatarMic({
    toggle: () => {
      // Mic on while the coach is mid-segment → pause so the learner can be heard
      // without overlap.
      if (coachSpeaking) {
        coachRef.current?.pause();
        setPaused(true);
      }
      coachRef.current?.microphoneToggle();
    },
    sessionReady: coachReady,
    onInterimText: (text) => setAskInput(text),
    onFinalText: (text) => {
      // Display only — the loader auto-asks on mic-final
      // (conversationConnection.js:200). Calling coachRef.current?.ask(text)
      // here would double-send.
      pendingAskRef.current = text;
      setTranscript((prev) => [...prev, { id: `learner-${Date.now()}`, role: 'learner', text }]);
      setAskInput('');
    },
  });

  // Mini-bubble (brief §2): on narrow viewports, the coach shrinks to a fixed
  // top-right bubble once the learner scrolls past the first fold. The
  // `coach-stage--mini` class is a no-op above 640px — CSS enforces the
  // scope. This state just tracks scroll so the class can apply when needed.
  const [isMiniStage, setIsMiniStage] = useState(false);
  useEffect(() => {
    const FOLD_THRESHOLD = 220;
    const onScroll = () => setIsMiniStage(window.scrollY > FOLD_THRESHOLD);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Gates the auto-speak effect — browser autoplay needs a user gesture in
  // live mode. Any action that `ask()`s the coach (Start button, Ask-coach text,
  // Ask-coach mic) flips this to true.
  const [lessonStarted, setLessonStarted] = useState(false);

  // Latest CoachMeta classification — drives the CTA chip and (eventually)
  // caption styling. Reset to null when the beat changes so suggestions don't
  // bleed across beats.
  const [coachMeta, setCoachMeta] = useState<CoachMeta | null>(null);

  // Q&A transcript — accumulates user asks + the coach's ask-driven replies.
  // Scripted beat narration (playResponse-driven) is NOT recorded; that's
  // already shown in the panel/caption above. `pendingAskRef` holds the most
  // recent learner ask so the next dialog-flow response can be paired with it
  // and skip echoes from playResponse.
  const [transcript, setTranscript] = useState<ChatTurn[]>([]);
  const pendingAskRef = useRef<string | null>(null);

  // Caption reflects whatever the coach most recently spoke (via onResponse).
  // Defaults per beat so teaching beats have content even before play.
  const currentBeat = lesson.beats[beatIndex]!;
  const beatDefaultCaption = useMemo(() => defaultCaptionFor(currentBeat), [currentBeat]);
  const [captionText, setCaptionText] = useState(beatDefaultCaption);
  useEffect(() => {
    setCaptionText(beatDefaultCaption);
    setCoachMeta(null);
  }, [beatDefaultCaption]);

  // Check-in state
  const [checkInResponseText, setCheckInResponseText] = useState('');
  const [checkInAttempts, setCheckInAttempts] = useState(0);
  const [checkInVerdict, setCheckInVerdict] = useState<CheckInVerdict | null>(null);
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);

  // Reset check-in state whenever we land on a different check-in beat
  useEffect(() => {
    if (currentBeat.type === 'check_in') {
      setCheckInResponseText('');
      setCheckInAttempts(0);
      setCheckInVerdict(null);
      setCheckInSubmitting(false);
    }
  }, [currentBeat.id, currentBeat.type]);

  const navigate = useNavigate();
  const isLast = beatIndex === lesson.beats.length - 1;
  const progressPct = Math.round(((beatIndex + 1) / lesson.beats.length) * 100);

  // Coach startData — stable across beat changes, so AvatarMount doesn't remount.
  const startData = useMemo(() => buildLessonStartData(learner, lesson), [learner, lesson]);

  // Tell the coach's primary which beat the learner is on whenever it changes.
  // The session persists across navigations, so without this the coach's
  // dialog-flow context never updates beyond the initial mount.
  useEffect(() => {
    if (!coachReady) return;
    coachRef.current?.notifyContext({
      type: 'navigation to',
      description: `The learner is on lesson "${lesson.title}", beat ${beatIndex + 1} of ${lesson.beats.length} (${currentBeat.type}: "${currentBeat.title}").`,
      url: `/lesson/${lesson.lesson_id}`,
    });
  }, [
    coachReady,
    lesson.lesson_id,
    lesson.title,
    lesson.beats.length,
    beatIndex,
    currentBeat.type,
    currentBeat.title,
  ]);

  // Have the coach speak the current teaching/coaching beat's script when it becomes
  // current. Gated on `lessonStarted` so the first play requires a user gesture,
  // satisfying browser autoplay policy in live mode.
  const lastSpokenBeatRef = useRef<string | null>(null);
  useEffect(() => {
    if (!coachReady || !lessonStarted) return;
    if (lastSpokenBeatRef.current === currentBeat.id) return;
    if (currentBeat.type === 'teaching' || currentBeat.type === 'coaching_moment') {
      // Scripted teaching content — playResponse bypasses the dialog flow's
      // LLM and feeds the exact text to TTS (single-source rule).
      coachRef.current?.playResponse(currentBeat.coach_script);
      lastSpokenBeatRef.current = currentBeat.id;
    } else if (currentBeat.type === 'check_in' && currentBeat.coach_intro) {
      // The coach frames the prompt out loud so the learner doesn't sit in
      // silence reading the challenge cold. Scripted, so playResponse, not
      // ask. Optional — beats without a coach_intro stay silent.
      coachRef.current?.playResponse(currentBeat.coach_intro);
      lastSpokenBeatRef.current = currentBeat.id;
    }
  }, [coachReady, lessonStarted, currentBeat]);

  const onJump = (i: number) => {
    setBeatIndex(i);
  };

  const onContinue = () => {
    if (isLast) {
      navigate('/');
      return;
    }
    setBeatIndex((i) => Math.min(i + 1, lesson.beats.length - 1));
  };

  const onTogglePause = () => {
    if (paused) {
      coachRef.current?.play();
      setPaused(false);
    } else {
      coachRef.current?.pause();
      setPaused(true);
    }
  };

  const onReplay = () => {
    if (currentBeat.type !== 'teaching' && currentBeat.type !== 'coaching_moment') return;
    setPaused(false);
    coachRef.current?.playResponse(currentBeat.coach_script);
  };

  const onAskCoachSubmit = () => {
    const text = askInput.trim();
    if (!text) return;
    if (!lessonStarted) setLessonStarted(true);
    coachRef.current?.ask(text);
    pendingAskRef.current = text;
    setTranscript((prev) => [...prev, { id: `learner-${Date.now()}`, role: 'learner', text }]);
    setAskInput('');
    setPaused(false);
  };

  const onMicTap = () => {
    if (mic.state !== 'listening' && !lessonStarted) setLessonStarted(true);
    mic.onTap();
  };

  const onStartLesson = () => {
    setLessonStarted(true);
  };

  const onCheckInSubmit = (text: string) => {
    if (currentBeat.type !== 'check_in') return;
    if (checkInSubmitting) return;
    if (!lessonStarted) setLessonStarted(true);
    const nextAttempts = checkInAttempts + 1;
    const evaluation = evaluateCheckIn(text, currentBeat, nextAttempts);
    setCheckInResponseText(text);
    setCheckInAttempts(nextAttempts);
    setCheckInVerdict(evaluation.verdict);
    setCheckInSubmitting(true);
    setPaused(false);
    // evaluateCheckIn composes the coach's reply client-side from the rubric, so
    // the text is scripted — route via playResponse, not ask.
    coachRef.current?.playResponse(evaluation.coach_message);
  };

  /* --------------------------- Avatar callbacks ---------------------- */

  const onPlayerBuilt = () => setCoachReady(true);

  const onResponse = (r: { answerText: string }) => {
    const text = r.answerText;
    if (!text) return;
    setCoachSpeaking(true);
    if (pendingAskRef.current != null) {
      // Q&A reply paired with the user's ask — goes into the chat transcript
      // ONLY. Caption keeps showing the current beat's narration so lesson
      // content doesn't get pushed out by the conversation.
      setTranscript((prev) => [...prev, { id: `coach-${Date.now()}`, role: 'coach', text }]);
      pendingAskRef.current = null;
    } else {
      // No pending ask → this is scripted beat narration arriving via
      // playResponse. Drives the caption above the panel.
      setCaptionText(text);
    }
  };

  const onSegmentEnded = () => {
    setCoachSpeaking(false);
    setCheckInSubmitting(false);
  };

  const onMeta = (m: CoachMeta) => setCoachMeta(m);

  const canAdvanceCheckIn =
    currentBeat.type === 'check_in' && (checkInVerdict === 'strong' || checkInAttempts >= 2);

  return (
    <div className="lesson">
      {!lessonStarted && (
        <StartSessionModal
          eyebrow={`Module ${lesson.module} · Lesson ${lesson.lesson_number}`}
          headline={lesson.title}
          body={`${COACH.firstName} will open with the first beat. You can pause, replay, or ask him anything along the way.`}
          ctaLabel="Start lesson"
          ready={coachReady}
          onStart={onStartLesson}
        />
      )}
      <LessonTopBar lesson={lesson} progressPct={progressPct} />
      <div className="lesson__stage">
        <section className="lesson__main">
          <BeatHeader beat={currentBeat} />
          <MainPanel
            beat={currentBeat}
            checkInState={{
              attempts: checkInAttempts,
              verdict: checkInVerdict,
              submitting: checkInSubmitting,
              lastResponseText: checkInResponseText,
            }}
            onCheckInSubmit={onCheckInSubmit}
            onCheckInContinue={onContinue}
            canAdvanceCheckIn={canAdvanceCheckIn}
            captionText={captionText}
          />
          {currentBeat.type !== 'check_in' && (
            <PlaybackRow
              paused={paused}
              speaking={coachSpeaking}
              speed={speed}
              isLast={isLast}
              onTogglePause={onTogglePause}
              onReplay={onReplay}
              onToggleSpeed={() => setSpeed((s) => (s === '1x' ? '1.25x' : '1x'))}
              onContinue={onContinue}
            />
          )}
          <MetaCueChip meta={coachMeta} />
          <ChatTranscript turns={transcript} />
          <AskCoachInput
            value={askInput}
            onChange={setAskInput}
            onSubmit={onAskCoachSubmit}
            onMicTap={onMicTap}
            micState={mic.state}
            disabled={!coachReady}
          />
        </section>
        <CoachStage
          speaking={coachSpeaking && !paused}
          ready={coachReady}
          mini={isMiniStage}
          startData={startData}
          ref={coachRef}
          onPlayerBuilt={onPlayerBuilt}
          onResponse={onResponse}
          onSegmentEnded={onSegmentEnded}
          micProps={mic.avatarProps}
          onMeta={onMeta}
        />
      </div>
      <BeatsStrip lesson={lesson} currentIndex={beatIndex} onJump={onJump} />
      {mic.overlays}
    </div>
  );
}

function defaultCaptionFor(beat: LessonBeat): string {
  if (beat.type === 'teaching' || beat.type === 'coaching_moment') {
    return beat.coach_script;
  }
  return '';
}

/* ------------------------------- Top bar ------------------------------ */

function LessonTopBar({ lesson, progressPct }: { lesson: Lesson; progressPct: number }) {
  const m = findModule(lesson.module);
  return (
    <header className="lesson-topbar">
      <Link to="/" className="lesson-topbar__back">
        <ChevronLeft /> Back to Module {lesson.module}
        {m && <span className="lesson-topbar__back-sub"> · {m.title}</span>}
      </Link>
      <div className="lesson-topbar__center">
        <p className="lesson-topbar__eyebrow">
          Lesson {lesson.lesson_number} of {m?.lessons.length ?? '—'}
        </p>
        <h1 className="lesson-topbar__title">{lesson.title}</h1>
      </div>
      <div className="lesson-topbar__progress">
        <div className="lesson-topbar__progress-track">
          <div className="lesson-topbar__progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="lesson-topbar__progress-label">{progressPct}%</span>
      </div>
    </header>
  );
}

/* ----------------------------- Beat header ---------------------------- */

function BeatHeader({ beat }: { beat: LessonBeat }) {
  const label =
    beat.type === 'teaching'
      ? `${COACH.firstName} is teaching`
      : beat.type === 'check_in'
        ? 'Your turn'
        : 'Coaching moment';
  const color = beat.type === 'check_in' ? 'warmth' : 'coach';
  return (
    <div className={`beat-header beat-header--${color}`}>
      <span className="beat-header__dot" aria-hidden />
      <span className="beat-header__label">{label}</span>
      <span className="beat-header__title">· {beat.title}</span>
    </div>
  );
}

/* ------------------------------ Main panel ---------------------------- */

interface CheckInPanelState {
  attempts: number;
  verdict: CheckInVerdict | null;
  submitting: boolean;
  lastResponseText: string;
}

function MainPanel({
  beat,
  checkInState,
  onCheckInSubmit,
  onCheckInContinue,
  canAdvanceCheckIn,
  captionText,
}: {
  beat: LessonBeat;
  checkInState: CheckInPanelState;
  onCheckInSubmit: (text: string) => void;
  onCheckInContinue: () => void;
  canAdvanceCheckIn: boolean;
  captionText: string;
}) {
  if (beat.type === 'teaching') return <TeachingPanel beat={beat} captionText={captionText} />;
  if (beat.type === 'check_in')
    return (
      <CheckInPanel
        beat={beat}
        state={checkInState}
        captionText={captionText}
        canAdvance={canAdvanceCheckIn}
        onSubmit={onCheckInSubmit}
        onContinue={onCheckInContinue}
      />
    );
  return (
    <CoachingMomentPanel
      beat={beat}
      learnerResponse={checkInState.lastResponseText}
      captionText={captionText}
    />
  );
}

function TeachingPanel({ beat, captionText }: { beat: TeachingBeat; captionText: string }) {
  return (
    <div className="panel">
      <VisualAnchorView anchor={beat.visual} />
      <LiveCaption script={captionText || beat.coach_script} />
    </div>
  );
}

function CheckInPanel({
  beat,
  state,
  captionText,
  canAdvance,
  onSubmit,
  onContinue,
}: {
  beat: CheckInBeat;
  state: CheckInPanelState;
  captionText: string;
  canAdvance: boolean;
  onSubmit: (text: string) => void;
  onContinue: () => void;
}) {
  const [draft, setDraft] = useState('');
  // Pre-fill draft with the last submitted text on retry so learner can edit.
  useEffect(() => {
    if (state.verdict === 'weak' && !state.submitting && state.attempts < 2) {
      setDraft(state.lastResponseText);
    }
  }, [state.verdict, state.submitting, state.attempts, state.lastResponseText]);

  const disabled = state.submitting || canAdvance;

  return (
    <div className="panel">
      <div className="anchor anchor--challenge">
        <p className="anchor__eyebrow">{COACH.firstName} asks</p>
        <p className="anchor__challenge-q">{beat.challenge}</p>
      </div>
      <form
        className="checkin-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(draft);
        }}
      >
        <textarea
          className="checkin-form__input"
          placeholder={`Type your answer — ${COACH.firstName} will react as you go.`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          disabled={disabled}
        />
        {!canAdvance && (
          <button
            type="submit"
            className="checkin-form__submit"
            disabled={!draft.trim() || state.submitting}
          >
            {state.submitting
              ? `Waiting on ${COACH.firstName}…`
              : state.attempts === 0
                ? 'Submit'
                : 'Try again'}{' '}
            <ArrowRight />
          </button>
        )}
      </form>
      {captionText && (
        <div
          className={`checkin-verdict checkin-verdict--${
            state.verdict === 'strong' ? 'win' : state.verdict === 'weak' ? 'growth' : 'neutral'
          }`}
        >
          <p className="checkin-verdict__eyebrow">
            {COACH.firstName} · {state.verdict === 'strong' ? 'affirming' : 'redirecting'}
          </p>
          <p className="checkin-verdict__text">{captionText}</p>
        </div>
      )}
      {canAdvance && (
        <button
          type="button"
          className="playback__continue checkin-form__continue"
          onClick={onContinue}
        >
          Continue <ArrowRight />
        </button>
      )}
    </div>
  );
}

function CoachingMomentPanel({
  beat,
  learnerResponse,
  captionText,
}: {
  beat: CoachingMomentBeat;
  learnerResponse: string;
  captionText: string;
}) {
  const shown = learnerResponse || '(you skipped the check-in — showing an example answer.)';
  return (
    <div className="panel">
      <div className="anchor anchor--sideby">
        <div className="anchor__sideby-col">
          <p className="anchor__sideby-label anchor__sideby-label--you">Your answer</p>
          <p className="anchor__sideby-text">{shown}</p>
        </div>
        <div className="anchor__sideby-col anchor__sideby-col--sharp">
          <p className="anchor__sideby-label anchor__sideby-label--coach">Sharpened</p>
          <p className="anchor__sideby-text">{beat.sharpened_alternative}</p>
        </div>
      </div>
      <LiveCaption script={captionText || beat.coach_script} />
    </div>
  );
}

/* ----------------------------- Visual anchor --------------------------- */

function VisualAnchorView({ anchor }: { anchor: VisualAnchor }) {
  switch (anchor.kind) {
    case 'stat-grid':
      return <StatGridAnchor data={anchor.data} />;
    case 'quote':
      return <QuoteAnchor data={anchor.data} />;
    case 'side-by-side':
      return <SideBySideAnchor data={anchor.data} />;
    case 'challenge':
      return (
        <div className="anchor anchor--challenge">
          <p className="anchor__eyebrow">{COACH.firstName} asks</p>
          <p className="anchor__challenge-q">{anchor.data.question}</p>
        </div>
      );
  }
}

function StatGridAnchor({ data }: { data: Extract<VisualAnchor, { kind: 'stat-grid' }>['data'] }) {
  const dots = Array.from({ length: data.total }, (_, i) => i < data.filled);
  return (
    <div className="anchor anchor--statgrid">
      <p className="anchor__stat">{data.stat}</p>
      <div className="anchor__dots" aria-hidden>
        {dots.map((filled, i) => (
          <span key={i} className={`anchor__dot ${filled ? 'anchor__dot--filled' : ''}`} />
        ))}
      </div>
      <p className="anchor__caption">{data.caption}</p>
    </div>
  );
}

function QuoteAnchor({ data }: { data: Extract<VisualAnchor, { kind: 'quote' }>['data'] }) {
  return (
    <div className="anchor anchor--quote">
      <blockquote className="anchor__quote">{data.quote}</blockquote>
      {data.attribution && <p className="anchor__attribution">— {data.attribution}</p>}
    </div>
  );
}

function SideBySideAnchor({
  data,
}: {
  data: Extract<VisualAnchor, { kind: 'side-by-side' }>['data'];
}) {
  return (
    <div className="anchor anchor--sideby">
      <div className="anchor__sideby-col">
        <p className="anchor__sideby-label anchor__sideby-label--you">{data.left_label}</p>
        <p className="anchor__sideby-text">{data.left_text}</p>
      </div>
      <div className="anchor__sideby-col anchor__sideby-col--sharp">
        <p className="anchor__sideby-label anchor__sideby-label--coach">{data.right_label}</p>
        <p className="anchor__sideby-text">{data.right_text}</p>
      </div>
    </div>
  );
}

/* ---------------------------- Playback row ---------------------------- */

function PlaybackRow({
  paused,
  speaking,
  speed,
  isLast,
  onTogglePause,
  onReplay,
  onToggleSpeed,
  onContinue,
}: {
  paused: boolean;
  speaking: boolean;
  speed: '1x' | '1.25x';
  isLast: boolean;
  onTogglePause: () => void;
  onReplay: () => void;
  onToggleSpeed: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="playback">
      <button type="button" className="playback__ghost" onClick={onReplay}>
        <ReplayIcon /> Replay
      </button>
      <button
        type="button"
        className="playback__primary"
        onClick={onTogglePause}
        aria-pressed={paused}
        disabled={!speaking && !paused}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
        {paused ? 'Play' : 'Pause'}
      </button>
      <button type="button" className="playback__ghost" onClick={onToggleSpeed}>
        {speed}
      </button>
      <button type="button" className="playback__continue" onClick={onContinue}>
        {isLast ? 'Finish lesson' : 'Continue'} <ArrowRight />
      </button>
    </div>
  );
}

/* ----------------------------- Coach stage ---------------------------- */

interface CoachStageProps {
  speaking: boolean;
  ready: boolean;
  mini: boolean;
  startData: ReturnType<typeof buildLessonStartData>;
  onPlayerBuilt: () => void;
  onResponse: (r: { answerText: string }) => void;
  onSegmentEnded: () => void;
  micProps: {
    onMicrophoneData: (d: { text: string; isFinal: boolean }) => void;
    onMicrophoneListening: () => void;
    onMicrophoneStopped: () => void;
  };
  onMeta: (m: CoachMeta) => void;
}

const CoachStage = forwardRef<AvatarHandle, CoachStageProps>(function CoachStage(
  { speaking, ready, mini, startData, onPlayerBuilt, onResponse, onSegmentEnded, micProps, onMeta },
  ref,
) {
  return (
    <aside className={`coach-stage ${mini ? 'coach-stage--mini' : ''}`}>
      <div className="coach-stage__frame">
        <AvatarMount
          ref={ref}
          loaderId={AVATAR_IDS.coach}
          startData={startData}
          ariaLabel={`${COACH.fullName}, coach`}
          onPlayerBuilt={onPlayerBuilt}
          onResponse={onResponse}
          onSegmentEnded={onSegmentEnded}
          {...micProps}
          onMeta={onMeta}
        />
        <span
          className={`coach-stage__pill coach-stage__pill--${
            !ready ? 'loading' : speaking ? 'speaking' : 'waiting'
          }`}
        >
          {!ready ? (
            <>
              <span className="coach-stage__dot" aria-hidden /> Loading
            </>
          ) : speaking ? (
            <>
              <WaveformBars /> Speaking
            </>
          ) : (
            <>
              <span className="coach-stage__dot" aria-hidden /> Waiting
            </>
          )}
        </span>
      </div>
      <div className="coach-stage__caption">
        <p className="coach-stage__name">{COACH.fullName}</p>
        <p className="coach-stage__role">Your coach</p>
      </div>
    </aside>
  );
});

function WaveformBars() {
  return (
    <span className="waveform" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className="waveform__bar" style={{ animationDelay: `${i * 110}ms` }} />
      ))}
    </span>
  );
}

/* ----------------------------- Beats strip ---------------------------- */

function BeatsStrip({
  lesson,
  currentIndex,
  onJump,
}: {
  lesson: Lesson;
  currentIndex: number;
  onJump: (i: number) => void;
}) {
  return (
    <nav className="beats" aria-label="Lesson beats">
      {lesson.beats.map((b, i) => {
        const state = i < currentIndex ? 'complete' : i === currentIndex ? 'current' : 'upcoming';
        return (
          <button
            key={b.id}
            type="button"
            className={`beats__seg beats__seg--${state}`}
            onClick={() => onJump(i)}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="beats__bar" />
            <span className="beats__label">
              <span className="beats__n">{i + 1}</span>
              <span className="beats__title">{b.title}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/* -------------------------------- Icons ------------------------------- */

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" />
    </svg>
  );
}
function ReplayIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
